import { faIndustryWindows, faUser } from '@fortawesome/pro-solid-svg-icons';
import {
    ICorporationResponse,
    IDeploymentAndValueStats,
    IssueStatusType,
} from '@kcftech/prometheustypescriptapiclient';
import { useQuery } from '@tanstack/react-query';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { HardwareIcon } from 'components/svg_icon/HardwareIcon';
import { sdClient } from 'contexts/SDKClientContext';
import { useDebounce } from 'custom_hooks/useDebounce';
import { DashboardHeader } from 'pages/Dashboard/components/DashboardHeader';
import { LocationFilterButton } from 'pages/Dashboard/components/filter_button/LocationFilterButton';
import { NoData } from 'pages/Dashboard/components/NoData';
import { CardHeader } from 'pages/Dashboard/components/styled';
import { AssetCoverage } from 'pages/Dashboard/DeploymentAndValueDashboard/Charts/AssetCoverage';
import { DeploymentScope } from 'pages/Dashboard/DeploymentAndValueDashboard/Charts/DeploymentScope';
import { ValueBySavingsType } from 'pages/Dashboard/DeploymentAndValueDashboard/Charts/ValueBySavingsType';
import {
    parseMonthKey,
    ValueOverTime,
} from 'pages/Dashboard/DeploymentAndValueDashboard/Charts/ValueOverTime';
import { Count } from 'pages/Dashboard/DeploymentAndValueDashboard/components/Counts';
import {
    Card,
    CardContent,
    Page,
} from 'pages/Dashboard/DeploymentAndValueDashboard/components/styled';
import { useDeploymentFiltersStore } from 'pages/Dashboard/DeploymentAndValueDashboard/store/useDeploymentFiltersStore';
import {
    DashboardTimeRangePreset,
    presetToDateRange,
    TimeRangeFilterButton,
} from 'pages/Dashboard/DeskActivityOverviewDashboard/components/TimeRangeFilterButton';
import { StatusLozenge } from 'pages/Desk/issues/StatusLozenge';
import { SimpleTreeItemIcon } from 'pages/workbench/side_panel/workbench_tree/TreeItemGroupIcon';
import { memo, ReactNode, useCallback, useMemo } from 'react';
import { IssueMetricsContent } from 'shared/issue_metrics/IssueMetricsWidget';
import { round } from 'utils/number';
import { getCorporationSettingsRoute, getHardwareRoute } from 'utils/RouteHelper';
import { TimeSpan } from 'utils/TimeSpan';

type DashboardCardProps = {
    data: IDeploymentAndValueStats | undefined;
};

function IconWord({ icon, word }: { icon: ReactNode; word: string }) {
    return (
        <strong
            style={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: '0.2rem',
                marginInline: '0.2rem',
            }}
        >
            <span style={{ alignSelf: 'center', transform: 'translateY(5%)' }}>
                {icon}
            </span>
            {word}
        </strong>
    );
}

const CountsCard = memo(function CountsCard({
    data,
    corporationId,
}: DashboardCardProps & { corporationId: string }) {
    return (
        <Card className="counts" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            <Count
                title="Sites"
                value={data?.Aggregates.Sites}
                icon={faIndustryWindows}
                link={getCorporationSettingsRoute(corporationId, ['location'])}
                info={'Count of sites actively streaming data'}
            />
            <Count
                title="Users"
                value={data?.Aggregates.Users}
                icon={faUser}
                link={getCorporationSettingsRoute(corporationId, ['users'])}
                info="Count of users that have logged in and interacted with SMARTdiagnostics"
            />
            <Count
                title="Assets"
                value={data?.Aggregates.Assets}
                icon={<SimpleTreeItemIcon type="Asset" />}
                info={
                    <span>
                        Count of{' '}
                        <IconWord
                            icon={
                                <HardwareIcon
                                    type="asset"
                                    className="mr-1"
                                    style={{ alignSelf: 'center' }}
                                />
                            }
                            word="Assets"
                        />{' '}
                        in the system hierarchy
                    </span>
                }
            />
            <Count
                title="Active Sensors"
                value={data?.Aggregates.ActiveSensors}
                icon={<HardwareIcon type="sensor" />}
                link={getHardwareRoute(corporationId, undefined, [
                    'adminSetup',
                    'sensors',
                ])}
                info={
                    <span>
                        Number of{' '}
                        <IconWord
                            icon={
                                <HardwareIcon
                                    type="sensor"
                                    className="mr-1"
                                    style={{ alignSelf: 'center' }}
                                />
                            }
                            word="Sensors"
                        />{' '}
                        installed and assigned to a{' '}
                        <IconWord
                            icon={<SimpleTreeItemIcon type="MonitoringPoint" />}
                            word="monitoring point"
                        />
                    </span>
                }
            />
        </Card>
    );
});

const HighValueCountsCard = memo(function HighValueCountsCard({
    data,
}: DashboardCardProps) {
    return (
        <IssueMetricsContent
            className="value-counts"
            filteredMetrics={[
                {
                    type: 'issue-addressed',
                    label: 'Issues',
                    subLabel: 'Addressed',
                    value: data?.TimeRangedAggregates.IssuesClosed ?? 0,
                    description: (
                        <span>
                            Number of faults mitigated, as counted by Issues marked with a{' '}
                            <span className="ml-1 mr-1">
                                <StatusLozenge status={IssueStatusType.Closed} />
                            </span>{' '}
                            status
                        </span>
                    ),
                },
                {
                    type: 'downtime',
                    label: 'Downtime',
                    subLabel: 'Hours Saved',
                    value: data
                        ? round(
                              TimeSpan.parse(
                                  data.TimeRangedAggregates.DowntimeAvoided
                              ).asHours(),
                              0
                          )
                        : '', // should be null
                    description:
                        'Sum of downtime avoided across all value-documented issues',
                },
                {
                    type: 'savings',
                    label: 'Cost',
                    subLabel: 'Avoided',
                    value: data?.TimeRangedAggregates.CostAvoided ?? 0,
                    description: 'Total quantified savings in all categories',
                },
            ]}
            metricCount={3}
            error={null}
            isFetching={false}
            isLoading={!data}
            level="Corporation"
        />
    );
});

const DeploymentScopeCard = memo(function DeploymentScopeCard({
    data,
}: DashboardCardProps) {
    return (
        <Card className="deploy-scope">
            <CardHeader
                title="Deployment Scope"
                description={
                    <>
                        Number of
                        <IconWord
                            icon={
                                <HardwareIcon
                                    type="sensor"
                                    className="mr-1"
                                    style={{ alignSelf: 'center' }}
                                />
                            }
                            word="Sensors"
                        />{' '}
                        installed and assigned to a{' '}
                        <IconWord
                            icon={<SimpleTreeItemIcon type="MonitoringPoint" />}
                            word="monitoring points"
                        />{' '}
                        at each location
                    </>
                }
            />
            <SpinnerArea isLoading={!data?.ActiveSensorsByLocation} noContainer>
                {!!data?.ActiveSensorsByLocation && (
                    <DeploymentScope data={data.ActiveSensorsByLocation} />
                )}
            </SpinnerArea>
        </Card>
    );
});

const ValueByTypeCard = memo(function ValueByTypeCard({ data }: DashboardCardProps) {
    return (
        <Card className="value-by-type">
            <CardHeader
                title="Value by Type"
                description={
                    <>
                        Breakdown of monetary value saved across all value-documented
                        Issues, split by Savings category
                    </>
                }
            />
            <CardContent $contentPositioning="stretch">
                <SpinnerArea
                    isLoading={!data?.SavingsAmountBySavingsCategory}
                    noContainer
                    fullHeight={false}
                    fullWidth={false}
                >
                    {!!data?.SavingsAmountBySavingsCategory && (
                        <ValueBySavingsType data={data.SavingsAmountBySavingsCategory} />
                    )}
                </SpinnerArea>
            </CardContent>
        </Card>
    );
});

const AssetCoverageCard = memo(function AssetCoverageCard({ data }: DashboardCardProps) {
    return (
        <Card className="asset-coverage">
            <CardHeader
                title="Asset Coverage"
                description={
                    <>
                        Number of monitored{' '}
                        <IconWord
                            icon={<SimpleTreeItemIcon type="Asset" />}
                            word="Assets"
                        />
                        , split by Asset Type
                    </>
                }
            />
            <SpinnerArea isLoading={!data?.AssetsByAssetType} noContainer>
                {!!data?.AssetsByAssetType && (
                    <AssetCoverage data={data.AssetsByAssetType} />
                )}
            </SpinnerArea>
        </Card>
    );
});

const ValueOverTimeCard = memo(function ValueOverTimeCard({
    data,
    startDateTime,
    endDateTime,
}: DashboardCardProps & { startDateTime: Date; endDateTime: Date | undefined }) {
    const filteredMonthlySavings = useMemo(() => {
        if (!data?.MonthlySavings) {
            return undefined;
        }

        // Filter monthly savings to only include data points within the date range
        return data.MonthlySavings.filter((point) => {
            if (!point.Key) {
                return false;
            }
            const pointDate = parseMonthKey(point.Key);
            if (pointDate === null) {
                return false;
            }
            // Check if point is after startDateTime and before endDateTime (if defined)
            const afterStart = pointDate >= startDateTime;
            const beforeEnd = endDateTime ? pointDate <= endDateTime : true;
            return afterStart && beforeEnd;
        });
    }, [data?.MonthlySavings, startDateTime, endDateTime]);

    const hasData = !!filteredMonthlySavings && filteredMonthlySavings.length > 0;
    return (
        <Card className="value-over-time">
            <SpinnerArea isLoading={!data?.AssetsByAssetType} noContainer>
                <CardHeader
                    title="Value Over Time"
                    description={
                        <>
                            Total Isssue-recorded monetary value delivered over the
                            selected time period
                        </>
                    }
                />
                <CardContent $contentPositioning="center">
                    {hasData && <ValueOverTime data={filteredMonthlySavings} />}
                    {!hasData && <NoData />}
                </CardContent>
            </SpinnerArea>
        </Card>
    );
});

export type DeploymentAndValueDashboardProps = {
    corporation: ICorporationResponse;
};

export function DeploymentAndValueDashboard({
    corporation,
}: DeploymentAndValueDashboardProps) {
    const {
        startDateTime,
        endDateTime,
        locationIds,
        timeRangePreset,
        setStartDateTime,
        setEndDateTime,
        setLocationIds,
        setTimeRangePreset,
    } = useDeploymentFiltersStore();

    // Debounce the start date to avoid spamming the API while sliding
    const debouncedStartDateTime = useDebounce(startDateTime, 300);

    const handleTimeRangePresetChange = useCallback(
        (preset: DashboardTimeRangePreset) => {
            setTimeRangePreset(preset);

            const range = presetToDateRange(preset);
            setStartDateTime(range.startDateTime);
            setEndDateTime(range.endDateTime);
        },
        [setTimeRangePreset, setStartDateTime, setEndDateTime]
    );

    const { data, refetch: _refetch } = useQuery({
        queryKey: [
            'deployment-and-value-dashboard',
            corporation.Id,
            debouncedStartDateTime,
            endDateTime,
            locationIds,
        ],
        queryFn: () =>
            sdClient.getDeploymentAndValueStatsForCorporation({
                corporationId: corporation.Id,
                startDateTime: debouncedStartDateTime,
                endDateTime: endDateTime,
                locationIds,
            }),
        placeholderData: (prev) => prev,
    });

    return (
        <Page>
            <DashboardHeader
                corporation={corporation}
                currentDashboard="deployment-and-value"
            >
                <LocationFilterButton
                    corporationId={corporation.Id}
                    locationIds={locationIds}
                    onChangeLocationIds={setLocationIds}
                />
                <TimeRangeFilterButton
                    selectedPreset={timeRangePreset}
                    onChangePreset={handleTimeRangePresetChange}
                />
            </DashboardHeader>

            <HighValueCountsCard data={data} />
            <CountsCard data={data} corporationId={corporation.Id} />
            <DeploymentScopeCard data={data} />
            <ValueByTypeCard data={data} />
            <AssetCoverageCard data={data} />
            <ValueOverTimeCard
                data={data}
                startDateTime={startDateTime}
                endDateTime={endDateTime}
            />
        </Page>
    );
}
